import type { ProductCardProps } from "../../components/listing/mobile/ProductCard";
import type { Product, Store } from "../../types/ProductListing";
import { copyMarketplaceStoreLink } from "src/util/Marketplace";
import { calculateProductTotalStock } from "../../utils/stockCalculations";

type CardPropsOptions = {
  products: Product[];
  stores: Store[];
  onStockUpdate: (product: Product) => void;
  onPriceUpdate: (product: Product) => void;
};

const marketplaceLogo = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=32&h=32&fit=crop";

export const useProductListingCardProps = ({
  products,
  stores,
  onStockUpdate,
  onPriceUpdate,
}: CardPropsOptions) => {
  const handleMobileAction = (action: string, productId: string) => {
    const product = products.find((item) => item.id === productId);
    if (!product) return;
    if (product.marketplace === "tokopedia" && action !== "copy-link") return;
    if (action === "update-stock") return onStockUpdate(product);
    if (action === "adjust-price") return onPriceUpdate(product);
    if (action === "copy-link") {
      copyMarketplaceStoreLink(product.marketplace, product, stores.find((store) => store.id === product.accountId));
    }
  };

  const convertToCardProps = (product: Product): ProductCardProps => {
    const stock = calculateProductTotalStock(product);
    const store = stores.find((item) => item.id === product.accountId);
    const marketplace = ["shopee", "lazada", "tokopedia", "tiktok"].includes(product.marketplace)
      ? product.marketplace as "shopee" | "lazada" | "tokopedia" | "tiktok"
      : "shopee";

    return {
      id: product.id,
      name: product.title,
      sku: product.sku,
      image: product.mainImage,
      merchantProduct: product.merchantProduct,
      store: { name: store?.name || `${product.marketplace} Store`, logo: marketplaceLogo, marketplace },
      price: { amount: product.price.current / 100, currency: product.price.currency },
      stock: { quantity: stock, status: stock === 0 ? "out-of-stock" : stock < 50 ? "low-stock" : "in-stock" },
      engagement: {
        likes: Math.floor(product.ratings.totalRatingCount * 0.3),
        views: Math.floor(product.ratings.totalRatingCount * 2.5),
        rating: product.ratings.avgRating,
        reviewCount: product.ratings.totalRatingCount,
      },
      quality: product.sync.status === "success" ? "good" : product.sync.status === "pending" ? "incomplete" : "poor",
      syncStatus: product.sync.status,
      lastSyncTime: product.sync.lastSynced,
      onAction: handleMobileAction,
    };
  };

  return { convertToCardProps, handleMobileAction };
};
